<template>
    <connection-settings-popup
        :resources="{
            deletePromptTitle: resource('JourneyBuilder_FilterSettings_Delete_Title'),
            deletePromptBody: resource('JourneyBuilder_FilterSettings_Delete_Body'),
            deleteButtonText: resource('JourneyComposer_JourneyBrickMenu_DeleteConnection')
        }"

        @save="save()"
        @delete="deleteConnection()"
        @hidden="$emit('hidden')"
    >
        <template #title>
            <div>
                <h3>{{resource("JourneyComposer_ConnectionModal_Connection")}}</h3>

                <copy-id-button :id="connection.id" />
            </div>
        </template>

        <template #body>
            <template v-if="canBeConfigured">
                <h4>{{resource("JourneyBuilder_JourneyBrickMenu_Status_DisplayName")}}</h4>
                <div class="row mb-2">
                    <label for="active" class="col-2">{{resource("Bool_Active")}}</label>
                    <span class="col-10">
                        <input id="active" type="checkbox" v-model="active" :disabled="!canWrite" />
                    </span>

                    <error-message
                        v-if="inactiveMessage"
                        class="col-10 offset-2"
                        :message="inactiveMessage"
                    />
                </div>
                <div class="row mb-2">
                    <label for="activeFrom" class="col-2">{{resource("JourneyComposer_ConnectionModal_ActiveFrom")}}</label>
                    <span class="col-10">
                        <input type="date" class="form-control" v-model="activeFrom" :disabled="!canWrite" />
                    </span>
                </div>

                <template v-if="canWrite">
                    <hr />
                </template>
            </template>

            <template v-if="canWrite">
                <h4>{{resource("JourneyBuilder_JourneyBrick_CommentHeader")}}</h4>
                <new-comment :brickId="connectionCommentId" :id="'newcomment'" />
            </template>

            <template v-if="!connection.isNew">
                <hr />
                <h4>{{resource("JourneyBuilder_JourneyBrickMenu_Comments_DisplayName")}}</h4>
                <div class="comment-container">
                    <comments-section
                        :related-id="connection.id"
                        type="Connection"
                    />
                </div>
            </template>
            
            <deactivation-confirmation-popup 
                @cancel="cancelDeactivation()"
                @save="confirmDeactivation($event)"
                
                v-if="displayDeactivationConfirmation"
                
                :object-id="connection.id"
                object-type="connection"
            />
        </template>
    </connection-settings-popup>
</template>

<script>
import { mapMutations } from "vuex"; 

import brickTypes from "@core/Modules/JourneyBuilder/Bricks/brick-types";

import resources from '@core/Util/Mixins/resources';
import permissions from '@modules/JourneyBuilder/Mixins/journey-permissions';

import CopyIdButton from "@modules/JourneyBuilder/Components/Elements/CopyIdButton";
import ConnectionSettingsPopup from '../ConnectionSettingsPopup';
import { CommentsSection, NewComment } from '@modules/JourneyBuilder/Components/Composer/CanvasArea/Bricks/Comments';
import { DeactivationConfirmationPopup, deactivationHelpers } from "@modules/JourneyBuilder/Components/Composer/Deactivation";
import ErrorMessage from "@core/Components/Errors/ErrorMessage";

export default {
    components: {
        ErrorMessage,
        CommentsSection,
        CopyIdButton,
        NewComment,
        ConnectionSettingsPopup,
        DeactivationConfirmationPopup
    },
    
    mixins: [ resources, permissions ],

    props: {
        /** @type {JourneyBrickConnection} */
        connection: Object
    },

    data: () => ({
        active: false,
        activeFrom: null,
        
        displayDeactivationConfirmation: false,
        deactivationOptions: undefined
    }),

    computed: {
        connectionCommentId() {
            return `connection:${this.connection.id}:default`;
        },
        
        deactivationCommentId() {
            return `connection:${this.connection.id}:deactivation`;
        },
        
        canBeConfigured(){
            //https://datanext.atlassian.net/browse/DPN-3123
            //Connections leading to a parent journey brick may not be configured
            return !this.connection.destinationBrick.isParentJourneyBrick;
        },
        
        inactiveMessage() {
            //https://datanext.atlassian.net/browse/DPN-3588
            //Inactive connections outgoing from an AB test brick should display a warning message
            //informing the user of the consequences
            if(
                this.connection.sourceBrick.type === brickTypes.abTest &&
                !this.active
            ) {
                return this.resource('JourneyComposer_Connection_Inactive_OutgoingABTest');
            }
            
            return '';
        }
    },
    
    watch: {
        active(value) {
            //Reset our content making our comment content get ignored by our backend
            if(value) {
                this.setNewComment({ brickId: this.deactivationCommentId, content: undefined });
            }
            
            //Do nothing if our connection is already disabled
            //new connections should not have to worry about being deactivated as no records will be affected
            if(
                value || 
                this.connection.isDisabled ||
                this.connection.isNew
            ) {
                return;
            } 
            
            //Begin our deactivation flow
            this.displayDeactivationConfirmation = true;
        }
    },

    mounted() {
        const vm = this;
        
        if (vm.connection.startDate) {
            const split = vm.connection.startDate.split('-');
            const d = split[0].length == 1 ? '0' + split[0] : split[0];
            const m = split[1].length == 1 ? '0' + split[1] : split[1];
            const y = split[2];
            vm.activeFrom = `${y}-${m}-${d}`;
        }
        
        vm.active = !vm.connection.isDisabled;
        vm.deactivationOptions = vm.connection.config.deactivationOptions;
    },

    methods: {
        ...mapMutations('journeyBuilder/composer', [ 'setNewComment' ]),
        
        save() {
            let activeFrom = null;
            if (this.activeFrom) {
                const activeFromDate = new Date(this.activeFrom);
                const d = activeFromDate.getDate() < 10 ? "0" + activeFromDate.getDate() : activeFromDate.getDate();
                const m = activeFromDate.getMonth() < 9 ? "0" + (activeFromDate.getMonth() + 1) : (activeFromDate.getMonth() + 1);
                const y = activeFromDate.getFullYear(); 
                activeFrom = `${d}-${m}-${y}`;
            }

            this.$emit("change-status", { 
                active: this.active, 
                activeFrom: activeFrom,
                
                deactivationOptions: this.active ? undefined : this.deactivationOptions
            });
        },

        deleteConnection() {
            this.$emit('delete-connection', this.connection.id);
        },
        
        cancelDeactivation() {
            this.active = true;
            this.displayDeactivationConfirmation = false;
        },
        
        confirmDeactivation(deactivationOptions) {
            this.deactivationOptions = deactivationOptions;
            this.displayDeactivationConfirmation = false;

            const {
                action,
                pauseDurationType,
                pauseDuration,
                pauseFinishedObjectAction,
                pauseFinishedRecordAction
            } = deactivationOptions;
            
            let deactivationSummary = '';
            
            if(deactivationHelpers.willDeleteRecords(action, pauseFinishedObjectAction, pauseFinishedRecordAction)) {
                deactivationSummary = this.resource('JourneyComposer_Connection_Deactivation_SummaryWithDelete');
            } else {
                deactivationSummary = sprintf(this.resource('JourneyComposer_Connection_Deactivation_SummaryWithPause'), deactivationHelpers.formatPauseDurationLabel(pauseDurationType, pauseDuration));
            }

            this.setNewComment({ brickId: this.deactivationCommentId, content: deactivationSummary });
        }
    },
}
</script>

<style scoped>
    .comments-section {
        width: 100%;
    }
</style>